import React from 'react';
import { SegmentPanelProps, TopicSegmentProps, TopicSliceProps } from './types';
import style from './style.module.scss';
import cx from 'classnames';
import { Col, Row } from 'react-bootstrap';
import Icon from '@magoosh/gre/assets/icons';
import ExpansionPanel, { ExpansionPanelGroup } from '../../lib/expansion_panel';
import { GenericBadge } from '@magoosh/lib/info_badges';
import { useModalContext } from '@magoosh/context/modal_context';
import { useUserContext } from '@magoosh/context/user_context';
import { SecondaryButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { segmentPanelData } from './data';
import { paths } from 'config/path_helpers';
import TopicPerformanceExplanation from '../topic_performance_explanation';
import ContextualLink from '@magoosh/gre/app/contextual_link';

export const TopicSegment: React.FC<TopicSegmentProps> = (props) => {
  const { adaptivePrepData } = useUserContext();

  const {
    abilityLevel,
    applicableForPerformanceScoreRange,
    id,
    inStudyPlan,
    isComplete,
    name,
    url,
    relevanceToUser,
    userPerformanceScore,
    isTooEarlyToTell,
    subsection
  } = props;

  // TODO - remove
  const addATopic = () => {};

  const getSegmentPanelData: () => SegmentPanelProps = () => {
    if (isComplete) {
      return { ...segmentPanelData.completed, href: url };
    } else if (inStudyPlan) {
      return {
        ...segmentPanelData.inStudyPlan,
        href: paths.dashboard()
      };
    } else if (
      (relevanceToUser === 'High' || relevanceToUser === 'Moderate') &&
      (isTooEarlyToTell ||
        userPerformanceScore === null ||
        (userPerformanceScore >= applicableForPerformanceScoreRange[0] &&
          userPerformanceScore <= applicableForPerformanceScoreRange[1]))
    ) {
      return {
        ...segmentPanelData.notRecommended,
        secondaryCTACopy: adaptivePrepData.hasStudyPlan && (
          <>
            {adaptivePrepData.isTrial && (
              <>
                <FontAwesomeIcon icon="lock" />{' '}
              </>
            )}
            {segmentPanelData.notRecommended.secondaryCTACopy}
          </>
        ),
        secondaryCTAOnClick: addATopic
      };
    } else {
      return {
        ...segmentPanelData.unlikelyToContribute,
        secondaryCTACopy: adaptivePrepData.hasStudyPlan && (
          <>
            {adaptivePrepData.isTrial && (
              <>
                <FontAwesomeIcon icon="lock" />
              </>
            )}
            {segmentPanelData.notRecommended.secondaryCTACopy}
          </>
        ),
        secondaryCTAOnClick: addATopic
      };
    }
  };

  const secondaryCTAProps = (onClose) => {
    return {
      style: { color: style.gray },
      className: cx(
        'u-margin-L-s',
        (adaptivePrepData.isTrial) && secondaryCTAOnClick && style.disabled
      ),
      onClick: () => {
        secondaryCTAOnClick(id, subsection, onClose);
      },
      href: secondaryCTAOnClick ? null : href,
      target: '_blank'
    };
  };

  const { title, text, secondaryCTACopy, secondaryCTAOnClick, href } = getSegmentPanelData();

  return (
    <div className={cx(style.topicSegment, 'u-margin-V-s')}>
      <ExpansionPanel
        id={id}
        text={
          <>
            <div className={style.topicButton}>
              <div>
                <h4 className={cx(style.name, 'u-margin-A-n')}>{name}</h4>
                <p>Level: {abilityLevel}</p>
              </div>

              {(isComplete && (
                <GenericBadge className={cx(style.isComplete, style.badge, 'u-margin-A-n')} text="Complete" />
              )) ||
                (inStudyPlan && (
                  <GenericBadge
                    className={cx(style.inStudyPlanBadge, style.badge, 'u-margin-A-n')}
                    text="In Study Plan"
                  />
                ))}
            </div>
          </>
        }
        title={<h4 className={style.title}>{title}</h4>}>
        {(onClose) => (
          <>
            <>{text}</>
            <br />
            <SecondaryButton onClick={() => onClose()}>Okay</SecondaryButton>

            {secondaryCTACopy && (
              <ContextualLink {...secondaryCTAProps(onClose)}>
                {secondaryCTACopy}
              </ContextualLink>
            )}
          </>
        )}
      </ExpansionPanel>
    </div>
  );
};

export const TopicSlice: React.FC<TopicSliceProps> = (props) => {
  const { setModal } = useModalContext();

  const {
    icon,
    name,
    subheadline,
    relevanceToUser,
    significanceLevel,
    description,
    userLevel,
    userPerformanceScore,
    userPerformanceScoreIsAssumed,
    userScoreImprovementOpportunity,
    segments,
    subsection,
    children
  } = props;

  const isTooEarlyToTell =
    userPerformanceScoreIsAssumed || userPerformanceScore === 0 || userPerformanceScore === 1;

  const handleLearnMoreClicked = () => {
    setModal({
      header: 'Your Current Level and Score Improvement Opportunity Explained',
      body: <TopicPerformanceExplanation handleClose={() => setModal(null)} />
    });
  };

  return (
    <Row className={cx(style.topicSlice, 'u-padding-T-s')}>
      <Col md={6} className={cx(style.infoSection)}>
        <Icon iconType={icon} fill={style.iconSecondary} />
        <h4 className="u-margin-A-n">
          <strong>{name}</strong>
        </h4>

        <p>{subheadline ? subheadline : `This is a ${significanceLevel} significance topic on the GRE.`}</p>

        {description && (
          <div className={style.performanceBlock}>
            <p>{description}</p>
          </div>
        )}

        {userLevel && userPerformanceScore !== null && (
          <div className={style.performanceBlock}>
            <p>
              <strong>Your Current Level: </strong>
              <span>{isTooEarlyToTell ? 'Too Early To Tell' : `${userPerformanceScore} (${userLevel})`}</span>
            </p>
            <p>
              <strong>Score Improvement Opportunity: </strong>
              <span>{userScoreImprovementOpportunity}</span>
            </p>
            <ContextualLink style={{ color: style.grayDarker }} onClick={handleLearnMoreClicked}>
              Learn More
            </ContextualLink>
          </div>
        )}
      </Col>
      <Col md={6} className={style.segmentSection}>
        <ExpansionPanelGroup>
          <>
            {children
              ? children
              : segments.map((segment) => (
                  <TopicSegment
                    key={segment.id}
                    {...segment}
                    relevanceToUser={relevanceToUser}
                    userPerformanceScore={userPerformanceScore}
                    isTooEarlyToTell={isTooEarlyToTell}
                    subsection={subsection}
                  />
                ))}
          </>
        </ExpansionPanelGroup>
      </Col>
    </Row>
  );
};

export default TopicSlice;
